// Google Ads OAuth Authentication Service
// Handles OAuth 2.0 flow for Google Ads API integration

const GOOGLE_ADS_CLIENT_ID = '731027622622-0rpsloo1v3apc41iuvfk1a1qjddb3c37.apps.googleusercontent.com';
const GOOGLE_ADS_SCOPE = 'https://www.googleapis.com/auth/adwords';

export class GoogleAdsAuthService {
  /**
   * Get the OAuth authorization URL
   * @param state Optional state parameter for CSRF protection
   */
  static getAuthUrl(state?: string): string {
    const redirectUri = this.getRedirectUri();
    const params = new URLSearchParams({
      client_id: GOOGLE_ADS_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: GOOGLE_ADS_SCOPE,
      access_type: 'offline',
      prompt: 'consent', // Force consent to get refresh token
      ...(state && { state }),
    });
    
    const authUrl = `https://accounts.google.com/o/oauth2/auth?${params.toString()}`;
    
    // Log for debugging
    console.log('[GoogleAdsAuth] OAuth Authorization URL Generated:', {
      redirectUri,
      clientId: GOOGLE_ADS_CLIENT_ID,
      scope: GOOGLE_ADS_SCOPE,
      state,
      fullUrl: authUrl,
      timestamp: new Date().toISOString(),
    });
    
    return authUrl;
  }

  /**
   * Get the redirect URI based on current environment
   */
  static getRedirectUri(): string {
    // Check if we're on production (custom domain or Firebase hosting)
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const protocol = window.location.protocol;
      const port = window.location.port ? `:${window.location.port}` : '';
      const origin = `${protocol}//${hostname}${port}`;
      
      let redirectUri: string;
      
      // Production - Firebase hosting (most common)
      if (hostname === 'operationsfaircareplus.web.app' || hostname.endsWith('.web.app')) {
        redirectUri = 'https://operationsfaircareplus.web.app/auth/google-ads/callback';
      }
      // Production - custom domain (check for both with and without 'i' to catch typos)
      else if (hostname === 'operations.faircareplus.com' || hostname.includes('faircareplus.com')) {
        redirectUri = 'https://operations.faircareplus.com/auth/google-ads/callback';
      }
      // Check for common typo: farcareplus (missing 'i')
      else if (hostname.includes('farcareplus.com') && !hostname.includes('faircareplus.com')) {
        console.error('⚠️ TYPO DETECTED: Hostname contains "farcareplus" instead of "faircareplus"');
        redirectUri = 'https://operations.faircareplus.com/auth/google-ads/callback';
      }
      // Development/local - use current origin
      else {
        redirectUri = `${origin}/auth/google-ads/callback`;
      }

      // Log for debugging
      console.log('[GoogleAdsAuth] Redirect URI Detection:', {
        hostname,
        protocol,
        port,
        origin,
        detectedRedirectUri: redirectUri,
        timestamp: new Date().toISOString(),
      });

      return redirectUri;
    }
    
    // Fallback to Firebase hosting
    const fallback = 'https://operationsfaircareplus.web.app/auth/google-ads/callback';
    console.log('[GoogleAdsAuth] Using fallback redirect URI:', fallback);
    return fallback;
  }

  /**
   * Extract authorization code from callback URL
   */
  static getCodeFromCallback(): string | null {
    if (typeof window === 'undefined') return null;
    
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('code');
  }

  /**
   * Extract state from callback URL
   */
  static getStateFromCallback(): string | null {
    if (typeof window === 'undefined') return null;
    
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('state');
  }

  /**
   * Extract error from callback URL
   */
  static getErrorFromCallback(): string | null {
    if (typeof window === 'undefined') return null;
    
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('error');
  }

  /**
   * Exchange authorization code for tokens
   * This calls a Cloud Function for security (client secret stays on server)
   */
  static async exchangeCodeForToken(code: string): Promise<{
    success: boolean;
    message?: string;
  }> {
    // Import Firebase Functions
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('@/config/firebase');
    const exchangeToken = httpsCallable(functions, 'exchangeGoogleAdsToken');
    
    const result = await exchangeToken({ code });
    return result.data as { success: boolean; message?: string };
  }

  /**
   * Initiate OAuth flow
   */
  static initiateAuth(state?: string): void {
    const authUrl = this.getAuthUrl(state);
    
    // Log initiation
    console.log('[GoogleAdsAuth] Initiating OAuth flow:', {
      redirectUri: this.getRedirectUri(),
      state,
      timestamp: new Date().toISOString(),
    });
    
    window.location.href = authUrl;
  }
}

